import { useEffect, useState } from 'react';

import { useParams } from 'react-router-dom';
import { toast } from 'sonner';

import { OrgPermission } from '../../../../../../../backend/src/organisation/organisation-permission';
import { Icon } from '../../../../components/icon';
import { useUpdateOrganisation } from '@/api/mutations/useUpdateOrganisation';
import { useUpdateOrganisationBillingPeriod } from '@/api/mutations/useUpdateOrganisationBillingPeriod';
import { useUpdateOrganisationIsTrialPackage } from '@/api/mutations/useUpdateOrganisationIsTrialPackage';
import { useUpdateSlackChannel } from '@/api/mutations/useUpdateSlackChannel';
import useGetOrganisation from '@/api/queries/useGetOrganisation';
import { useGlobalAdminCheck } from '@/hooks/useGlobalAdminCheck';
import { Breadcrumb } from '@/new/components/breadcrumb';
import { PageContainer } from '@/new/components/page-container';
import { PageHeader } from '@/new/components/page-header';
import { PageTitle } from '@/new/components/page-title';
import { Button } from '@/new/components/shadcn/button';
import { Input } from '@/new/components/shadcn/input';
import { Label } from '@/new/components/shadcn/label';
import { Switch } from '@/new/components/shadcn/switch';
import { SubTitle } from '@/new/components/subtitle';

interface PermissionConfig {
  id: string;
  title: string;
  description: string;
  permission: OrgPermission;
}

const permissionConfigs: PermissionConfig[] = [
  {
    id: 'api-deck-toggle',
    title: 'Apideck Integration',
    description: 'Whether the Apideck integration is enabled for this organisation.',
    permission: OrgPermission.Apideck,
  },
  {
    id: 'anthropic-toggle',
    title: 'Anthropic Models',
    description: 'Whether or not Anthropic models (Claude) through Frankfurt are enabled for this organisation.',
    permission: OrgPermission.AnthropicModels,
  },
  {
    id: 'inconsistency-toggle',
    title: 'Inconsistencies',
    description:
      'Whether or not the Inconsistency feature is enabled in this organisation. This requires Anthropic models to be enabled.',
    permission: OrgPermission.Inconsistencies,
  },
  {
    id: 'gemini-toggle',
    title: 'Gemini Models',
    description: 'Whether or not Gemini models are enabled for this organisation.',
    permission: OrgPermission.GeminiModels,
  },
  {
    id: 'uk-only-toggle',
    title: 'UK Only',
    description: 'Whether the data is restricted to be processed in the UK only.',
    permission: OrgPermission.UkOnly,
  },
  {
    id: 'admin-approval-toggle',
    title: 'Admin Approval',
    description: 'Whether the admin approval feature is enabled for this organisation.',
    permission: OrgPermission.AdminApproval,
  },
  {
    id: 'require-2fa-toggle',
    title: 'Require 2FA non-SSO users',
    description: 'Require non-SSO users to enable two-factor authentication before accessing the platform.',
    permission: OrgPermission.Require2FANonSSO,
  },
  {
    id: 'feedback-toggle',
    title: 'Enable in-app feedback',
    description: 'Whether the in-app feedback pop-ups are enabled for this organisation.',
    permission: OrgPermission.Feedback,
  },
];

const isUsingOnboardingPackage = {
  id: 'onboarding-package-toggle',
  title: 'Onboarding Package',
  description:
    'Whether this organisation is currently on an onboarding package with increased usage limits. This will be indicated in the usage dashboard (and otherwise has no other effects).',
};

function dateToDatetimeLocal(d: Date): string {
  return new Date(d.getTime() - d.getTimezoneOffset() * 60000).toISOString().slice(0, -1);
}

function PermissionRow({
  permission,
  isEnabled,
  onCheckedChange,
  disabled,
}: {
  permission: Omit<PermissionConfig, 'permission'>;
  isEnabled: boolean;
  onCheckedChange: () => void;
  disabled?: boolean;
}) {
  return (
    <div className="flex max-w-4xl items-center gap-6">
      <Switch checked={isEnabled} disabled={disabled} id={permission.id} onCheckedChange={onCheckedChange} />

      <div className="space-y-1">
        <p className="text-base font-medium text-gray-700">{permission.title}</p>
        <p className="text-gray-500">{permission.description}</p>
      </div>
    </div>
  );
}

export function AdminManageOrganisation() {
  const { organisationId } = useParams();
  if (!organisationId) return null;

  useGlobalAdminCheck();

  const { data: organisation } = useGetOrganisation(organisationId);

  const { mutate: updateOrganisation } = useUpdateOrganisation(organisationId);
  const { mutate: updateBillingPeriod, isLoading: isUpdatingBillingPeriod } =
    useUpdateOrganisationBillingPeriod(organisationId);
  const { mutate: updateIsTrialPackage, isLoading: isUpdatingTrialPackage } =
    useUpdateOrganisationIsTrialPackage(organisationId);
  const { mutate: updateSlackChannel, isLoading: isUpdatingSlackChannel } = useUpdateSlackChannel(organisationId);

  const [maxCredits, setMaxCredits] = useState<number | undefined>();
  const [billingStartDate, setBillingStartDate] = useState<Date | undefined>();
  const [billingEndDate, setBillingEndDate] = useState<Date | undefined>();
  const [billingDetailsChanged, setBillingDetailsChanged] = useState(false);
  const [slackChannelId, setSlackChannelId] = useState<string>('');

  function togglePermission(permission: OrgPermission) {
    if (!organisation) return;

    let updatedPermissions = organisation.permissions.includes(permission)
      ? organisation.permissions.filter((perm) => perm !== permission)
      : [...organisation.permissions, permission];

    // Remove Inconsistency permission if AnthropicModels is disabled
    if (!updatedPermissions.includes(OrgPermission.AnthropicModels)) {
      updatedPermissions = updatedPermissions.filter((perm) => perm !== OrgPermission.Inconsistencies);
    }

    updateOrganisation({ permissions: updatedPermissions });

    toast.success('Organisation permissions updated');
  }

  useEffect(() => {
    setMaxCredits(organisation?.maxCredits ?? undefined);
    setBillingStartDate(organisation?.billingStartDate ? new Date(organisation.billingStartDate) : undefined);
    setBillingEndDate(organisation?.billingEndDate ? new Date(organisation?.billingEndDate) : undefined);
    setSlackChannelId(organisation?.slackChannelId ?? '');
  }, [organisation]);

  function handleBillingPeriodSubmit(e: React.FormEvent) {
    e.preventDefault();

    if (maxCredits === undefined || !billingStartDate || !billingEndDate) {
      toast.error(`Billing period fields can't be empty`);
      return;
    }

    updateBillingPeriod({
      maxCredits,
      billingStartDate: billingStartDate,
      billingEndDate: billingEndDate,
    });
  }

  useEffect(() => {
    if (!organisation) return setBillingDetailsChanged(false);

    if (
      organisation.maxCredits === null ||
      organisation.billingStartDate === null ||
      organisation.billingEndDate === null
    ) {
      return setBillingDetailsChanged(true);
    }

    if (
      maxCredits !== organisation.maxCredits ||
      billingStartDate?.getTime() !== new Date(organisation.billingStartDate).getTime() ||
      billingEndDate?.getTime() !== new Date(organisation.billingEndDate).getTime()
    ) {
      return setBillingDetailsChanged(true);
    }

    setBillingDetailsChanged(false);
  }, [organisation, maxCredits, billingStartDate, billingEndDate]);

  return (
    <>
      <Breadcrumb
        items={
          organisation
            ? [
                { href: '/admin/organisations', label: 'Admin', active: false },
                {
                  href: `/admin/organisations/${organisation.id}`,
                  label: organisation.name,
                  active: true,
                },
              ]
            : [{ href: '/admin/organisations', label: 'Admin', active: false }]
        }
      />

      <PageContainer>
        {!organisation ? (
          // TODO: improve skeleton/move into generic component
          <div className="pulse my-8 h-8 w-sm rounded-lg bg-gray-50"></div>
        ) : (
          <>
            <PageHeader>
              <PageTitle text={organisation.name} />
            </PageHeader>

            <SubTitle text="Permissions" iconClass="lucide--shield-user" />

            <section className="my-8 flex flex-col gap-5">
              {permissionConfigs.map((permission) => (
                <PermissionRow
                  key={permission.id}
                  permission={permission}
                  onCheckedChange={() => togglePermission(permission.permission)}
                  isEnabled={organisation.permissions.includes(permission.permission)}
                  disabled={
                    permission.permission === OrgPermission.Inconsistencies &&
                    !organisation.permissions.includes(OrgPermission.AnthropicModels)
                  }
                />
              ))}
            </section>

            <SubTitle text="Billing Information" iconClass="lucide--circle-dollar-sign" className="mt-10" />

            <div className="my-8">
              <PermissionRow
                permission={isUsingOnboardingPackage}
                isEnabled={organisation.isUsingTrialPackage}
                onCheckedChange={() => updateIsTrialPackage({ isUsingTrialPackage: !organisation.isUsingTrialPackage })}
              />
            </div>

            <form onSubmit={handleBillingPeriodSubmit} className="my-10 flex max-w-48 flex-col gap-3">
              <div className="flex w-108 items-center">
                <Label className="basis-1/2" htmlFor="max-credits">
                  Maximum Credits
                </Label>
                <Input
                  className="basis-1/2"
                  type="number"
                  id="max-credits"
                  value={maxCredits ?? ''}
                  onChange={(e) => setMaxCredits(Number(e.target.value))}
                  disabled={isUpdatingBillingPeriod}
                />
              </div>

              <div className="flex w-108 items-center">
                <Label className="basis-1/2" htmlFor="start-date">
                  Billing Period Start Date
                </Label>
                <Input
                  className="basis-1/2"
                  type="datetime-local"
                  id="start-date"
                  value={billingStartDate ? dateToDatetimeLocal(billingStartDate) : ''}
                  onChange={(e) => setBillingStartDate(new Date(e.target.value))}
                  disabled={isUpdatingBillingPeriod}
                />
              </div>

              <div className="flex w-108 items-center">
                <Label className="basis-1/2" htmlFor="end-date">
                  Billing Period End Date
                </Label>
                <Input
                  className="basis-1/2"
                  type="datetime-local"
                  id="end-date"
                  value={billingEndDate ? dateToDatetimeLocal(billingEndDate) : ''}
                  onChange={(e) => setBillingEndDate(new Date(e.target.value))}
                  disabled={isUpdatingBillingPeriod}
                />
              </div>

              <Button
                variant="primary"
                type="submit"
                disabled={isUpdatingBillingPeriod || !billingDetailsChanged}
                className="my-3 w-fit"
              >
                <Icon className="lucide--banknote-arrow-up" />
                Update Billing Period
              </Button>
            </form>

            <SubTitle text="Settings" iconClass="lucide--message-square" className="mt-10" />

            <form
              onSubmit={(e) => {
                e.preventDefault();
                updateSlackChannel(
                  { slackChannelId: slackChannelId.trim() },
                  {
                    onSuccess: () => {
                      toast.success('Slack channel updated');
                    },
                  },
                );
              }}
              className="my-8 flex flex-col gap-3"
            >
              <div className="flex w-108 items-center">
                <Label className="basis-1/2" htmlFor="slack-channel-id">
                  Slack Channel ID
                </Label>
                <Input
                  className="basis-1/2"
                  type="text"
                  id="slack-channel-id"
                  placeholder="e.g. C0123456789"
                  value={slackChannelId}
                  onChange={(e) => setSlackChannelId(e.target.value)}
                  disabled={isUpdatingSlackChannel}
                />
              </div>
              <p className="text-sm text-gray-500">
                Messages about runs and org events will be routed to this Slack channel. Leave empty to use the default
                monitoring channel.
              </p>
              <Button
                variant="primary"
                type="submit"
                disabled={isUpdatingSlackChannel || slackChannelId === (organisation?.slackChannelId ?? '')}
                className="my-3 w-fit"
              >
                <Icon className="lucide--save" />
                Update Slack Channel
              </Button>
            </form>
          </>
        )}
      </PageContainer>
    </>
  );
}
